import { StartupScreen } from "@cliqsuite/ui";
import { useEffect, useState } from "react";
import { useParams, Outlet, useNavigate } from "@tanstack/react-router";
import { fromRow, syncApiUrl, setAuthToken } from "@cliqsuite/db/powersync";
import { GuestSessionProvider } from "../lib/session";
import { guestStoreSchema, useRows, type GuestStore } from "../lib/db";
import { Empty, Head, Shell } from "../components/Shell";

/**
 * Layout for every `/store/$storeId/...` screen. Opens a guest session for the
 * store (POST /auth/guest-session returns the scope token and the store row) and
 * wraps its subtree in GuestSessionProvider (cart, table, loyalty member, etc).
 * Keyed by storeId so switching stores from the directory resets the session.
 */
export function StoreLayout() {
  const { storeId } = useParams({ strict: false }) as { storeId: string };
  const navigate = useNavigate();
  const [opened, setOpened] = useState<{ storeId: string; store: GuestStore | null; ok: boolean } | "error" | null>(null);
  // Once synced, the local row keeps the store live (e.g. the owner turning guest ordering off).
  const { data: live } = useRows("stores", "SELECT * FROM stores WHERE id = ?", [storeId]);

  useEffect(() => {
    let cancelled = false;
    setOpened(null);
    setAuthToken(null);
    void fetch(syncApiUrl("/auth/guest-session"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ storeId }),
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as { ok?: boolean; scopeToken?: string; store?: Record<string, unknown> };
        if (res.status >= 500) throw new Error(`guest session ${res.status}`);
        if (cancelled) return;
        if (body.ok && body.scopeToken) setAuthToken(body.scopeToken);
        setOpened({ storeId, store: body.store ? { ...fromRow(guestStoreSchema, body.store), online: body.store.online as GuestStore["online"], pay_accounts: body.store.pay_accounts as GuestStore["pay_accounts"], pay_methods: body.store.pay_methods as GuestStore["pay_methods"] } : null, ok: !!body.ok });
      })
      .catch(() => {
        if (!cancelled) setOpened("error");
      });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const home = () => navigate({ to: "/" });
  const shown = opened && opened !== "error" && opened.storeId === storeId ? opened : null;
  const store = shown ? (live[0] ? { ...live[0], online: shown.store?.online, pay_accounts: shown.store?.pay_accounts, pay_methods: shown.store?.pay_methods } : shown.store) : null;

  if (!shown && opened !== "error") return <StartupScreen label="Opening the menu" />;
  if (!shown || !store || !shown.ok || !store.guest_portal_enabled || !store.is_active) {
    const [icon, title, desc] =
      opened === "error"
        ? ["wifi_off", "Couldn't open this store", "Check your connection and try again. If it keeps happening, ask the staff."]
        : !shown
          ? ["storefront", "Opening the store…", undefined]
          : !store
            ? ["storefront", "This store isn't available", "The link may be old. Find the store again from Discover."]
            : // The directory hides these stores, but a bookmarked link or a printed table QR still lands here.
              ["no_meals", "Ordering is off right now", `${store.name} isn't taking orders on the phone right now. Order at the counter or ask the staff.`];
    return (
      <Shell head={<Head back={home} icon={store?.store_icon ?? "storefront"} title={store?.name ?? "CliqSuite"} sub={store?.address ?? undefined} />}>
        <Empty
          icon={icon}
          title={title}
          desc={desc}
          action={
            opened === "error" ? (
              <button type="button" className="g-btn ink" onClick={() => window.location.reload()}>
                Try again
              </button>
            ) : undefined
          }
        />
      </Shell>
    );
  }

  return (
    <GuestSessionProvider key={store.id} store={store}>
      <Outlet />
    </GuestSessionProvider>
  );
}
